import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DevicesService } from '../../../services/devices.service';
import { LoginUserModel } from '../../../models/loginUserModel';
import { Device } from '../../../models/device';

interface HourCell { hour: number; active: boolean; }
interface DayRow { label: string; key: string; hours: HourCell[]; checked: boolean; }

@Component({
  selector: 'app-device-schedule-form',
  imports: [CommonModule, ToggleSwitchModule, FormsModule, ButtonModule, TooltipModule],
  templateUrl: './device-schedule-form.component.html',
  styleUrl: './device-schedule-form.component.scss'
})
export class DeviceScheduleFormComponent {
  @Input() device!: Device;
  @Output() onSaving = new EventEmitter<boolean>();
  @Output() onResponse = new EventEmitter<any>();

  data!: LoginUserModel;
  enabledSchedule = true;
  drawMouseEnter = false;
  showMessageError = false;
  messagesError = '';
  hours = Array.from({ length: 24 }, (_, i) => i);
  days: DayRow[] = [];

  // Group info
  groupName: string | null = null;
  groupSchedule: DayRow[] | null = null;

  private scheduleMap: { key: string; label: string; prop: string }[] = [
    { key: 'mon', label: 'Lunes', prop: 'mondaySchedule' },
    { key: 'tue', label: 'Martes', prop: 'tuesdaySchedule' },
    { key: 'wed', label: 'Miércoles', prop: 'wednesdaySchedule' },
    { key: 'thu', label: 'Jueves', prop: 'thursdaySchedule' },
    { key: 'fri', label: 'Viernes', prop: 'fridaySchedule' },
    { key: 'sat', label: 'Sábado', prop: 'saturdaySchedule' },
    { key: 'sun', label: 'Domingo', prop: 'sundaySchedule' },
  ];

  get isAdmin() { return this.data.UserType === 'Administrator' || this.data.UserType === 'Modify'; }

  constructor(private service: DevicesService) {
    this.data = JSON.parse(localStorage.getItem('data')!);
  }

  ngOnInit() {
    this.enabledSchedule = this.isAdmin ? !this.device.hasLoadSchedule : true;
    this.days = this.scheduleMap.map(s => this.buildDayRow(s.label, s.key, (this.device as any)[s.prop]));
    this.loadGroupInfo();
  }

  /** Build a day row from the device schedule object (hour00..hour23) */
  private buildDayRow(label: string, key: string, schedule: any): DayRow {
    const hours: HourCell[] = this.hours.map(h => ({
      hour: h,
      active: schedule ? schedule['hour' + h.toString().padStart(2, '0')] : false
    }));
    return { label, key, hours, checked: hours.every(h => h.active) };
  }

  /** Load group assignment for this device to show group schedule comparison */
  private loadGroupInfo() {
    this.service.getGroups(this.data.Client_id).subscribe({
      next: (resp: any) => {
        const groups = resp.response || [];
        // Check each group to find if this device is assigned
        groups.forEach((g: any) => {
          this.service.getGroupCards(this.data.Client_id, g.groupId).subscribe({
            next: (r: any) => {
              const assigned = r.response?.assignedList?.items || [];
              if (assigned.some((c: any) => c.cardId === this.device.cardId)) {
                this.groupName = g.description;
              }
            }
          });
        });
      }
    });
  }

  // --- Interactions ---

  changeState(cell: HourCell) {
    if (!this.enabledSchedule) {
      this.drawMouseEnter = true;
      cell.active = !cell.active;
    }
  }

  drawCell(cell: HourCell) {
    if (this.drawMouseEnter) cell.active = true;
  }

  stopDraw() { this.drawMouseEnter = false; }

  toggleDay(day: DayRow) {
    day.checked = !day.checked;
    day.hours.forEach(h => h.active = day.checked);
  }

  toggleHourColumn(hour: number) {
    const allActive = this.days.every(d => d.hours[hour].active);
    this.days.forEach(d => d.hours[hour].active = !allActive);
  }

  // --- Save ---

  saveSchedule() {
    this.showMessageError = false;
    this.onSaving.emit(true);

    if (this.enabledSchedule) {
      // No restriction
      this.service.saveCardParams(this.device, this.data.Client_number, this.data.User_id, 'SinLimiteHorarios', this.userName).subscribe({
        next: (resp: any) => this.handleResponse(resp),
        error: () => this.handleError()
      });
    } else {
      // Write hours back to device schedule objects
      this.scheduleMap.forEach((s, i) => {
        const schedule = (this.device as any)[s.prop];
        this.days[i].hours.forEach(h => {
          schedule['hour' + h.hour.toString().padStart(2, '0')] = h.active;
        });
      });
      this.service.saveCardParams(this.device, this.data.Client_number, this.data.User_id, 'Horarios', this.userName).subscribe({
        next: (resp: any) => this.handleResponse(resp),
        error: () => this.handleError()
      });
    }
  }

  private get userName() {
    return this.data.UserType === 'Administrator' ? 'Administrador de la cuenta' : this.data.Name;
  }

  private handleResponse(resp: any) {
    this.onSaving.emit(false);
    if (resp.errorCode === 1) {
      this.showMessageError = true;
      this.messagesError = resp.errorDesc;
    } else {
      this.onResponse.emit(resp);
    }
  }

  private handleError() {
    this.onSaving.emit(false);
    this.showMessageError = true;
    this.messagesError = 'Error al guardar los horarios. Intente nuevamente.';
  }
}
